import { Injectable } from '@angular/core';
import { AzureSearchConfig, AzureSearchService, AzureServiceType, Config, DropdownFacet, DropdownFacetItem, RangeFacet } from './azure-search.service';
import { ASSET_SEARCH_FACETS } from '../configs/assets-search-facets.config';
import { OrganizationService } from 'src/app/@shared';
import { AssetsListService, FacetType, initialSearchState, SearchAsset, SearchState } from '..';
import { BehaviorSubject, lastValueFrom, map, retry } from 'rxjs';

@Injectable({
    providedIn: 'root',
})

export class AzureAssetsSearchService {
    private readonly DEFAULT_SORT_FIELD = 'dateAddedEPOCH';
    private readonly DEFAULT_SORT_DIRECTION: 'asc' | 'desc' = 'desc';

    private searchStateParamsSubject = new BehaviorSubject<SearchState>(initialSearchState);
    private totalAssetsCountSubject = new BehaviorSubject<number>(0);
    private loadingSubject = new BehaviorSubject<boolean>(true);
    private searchQuerySubject = new BehaviorSubject<string>('');
    private assetsSubject = new BehaviorSubject<SearchAsset[]>([]);
    private assetGroupsSubject = new BehaviorSubject<DropdownFacetItem[]>([]);
    private assetTypesSubject = new BehaviorSubject<DropdownFacetItem[]>([]);
    private colorChannelsSubject = new BehaviorSubject<DropdownFacetItem[]>([]);
    private fileTypesSubject = new BehaviorSubject<DropdownFacetItem[]>([]);
    private filterBehaviorSubject = new BehaviorSubject<any>(null);
    private productCategoriesSubject = new BehaviorSubject<DropdownFacetItem[]>([]);

    searchStateParams$ = this.searchStateParamsSubject.asObservable();
    totalAssetsCount$ = this.totalAssetsCountSubject.asObservable();
    loading$ = this.loadingSubject.asObservable();
    searchQuery$ = this.searchQuerySubject.asObservable();
    assets$ = this.assetsSubject.asObservable();
    assetGroups$ = this.assetGroupsSubject.asObservable();
    assetTypes$ = this.assetTypesSubject.asObservable();
    colorChannels$ = this.colorChannelsSubject.asObservable();
    fileTypes$ = this.fileTypesSubject.asObservable();
    productCategories$ = this.productCategoriesSubject.asObservable();

    private azureSearchService!: AzureSearchService;

    assetGroupsFacet!: DropdownFacet;
    assetTypesFacet!: DropdownFacet;
    colorChannelsFacet!: DropdownFacet;
    fileTypesFacet!: DropdownFacet;
    productCategoriesFacet!: DropdownFacet;

    rangeInvalid = false;
    assetDateRangeFacet!: RangeFacet;
    uploadStartDate: Date | null = null;
    uploadEndDate: Date | null = null;

    constructor(
        private assetsListService: AssetsListService,
        private organizationService: OrganizationService
    ) { }

    async init() {
        if (this.azureSearchService) {
            return;
        }

        await this.fetchAzureSearchMetaData();

        const meta = this.organizationService.azureSearchMetaData;
        if (!meta) {
            console.error('Azure Search metadata missing');
            return;
        }

        const { SearchIndexName: index, QueryKey: queryKey, SearchEndPoint: service } = meta;

        const config: Config = {
            index,
            queryKey,
            service,
            type: AzureServiceType.Search,
            dateToValidate: null
        };

        this.azureSearchService = new AzureSearchService(config);

        const searchStateParams = this.searchStateParamsSubject.getValue();

        this.azureSearchService.updateSearchParameters({
            count: true,
            searchMode: 'all',
            top: searchStateParams.pageSize
        });

        this.azureSearchService.updateSuggestionsParameters({
            suggesterName: "sg",
            select: "assetId,assetClientKey"
        });

        this.azureSearchService.setResultsProcessor(this.searchResultCB);
        this.azureSearchService.renderDropdownFacetsCB = this.renderDropdownFacetsCB;
        this.azureSearchService.renderDateRangeFacetsCB = this.renderDateRangeFacetsCB;

        this.initFacets();
    }

    private async fetchAzureSearchMetaData() {
        if (!this.organizationService.azureSearchMetaData) {
            try {
                const metaData: AzureSearchConfig = await lastValueFrom(this.assetsListService.GetSearchMetaData().pipe(retry({ count: 5, delay: 1000 })));
                this.organizationService.azureSearchMetaData = metaData;
            } catch (error) {
                console.error('API failed after 5 retries:', error);
            }
        }
    }

    private initFacets() {
        this.azureSearchService.facetSearchEnabled = false;
        ASSET_SEARCH_FACETS.forEach(facet => {
            if (facet.type === 'dropdown') {
                const c = facet.config;
                this.azureSearchService.addDropdownFacet(
                    c.htmlId,
                    c.fieldName,
                    c.fieldTitle,
                    c.dataType
                );
            }

            if (facet.type === 'range') {
                const c = facet.config;
                this.azureSearchService.addDateRangeFacet(
                    c.htmlId,
                    c.fieldName,
                    c.fieldTitle,
                    c.dataType,
                    c.minValue,
                    c.maxValue
                );
            }
        });
    }

    private searchResultCB = (data: any) => {
        this.assetsSubject.next(data.results);
        this.totalAssetsCountSubject.next(data.count);
        this.loadingSubject.next(false);
    }

    private renderDropdownFacetsCB = (facet: DropdownFacet) => {
        const values: DropdownFacetItem[] = Object.values(facet.values);

        const facetConfigMap: Record<string, {
            subject: BehaviorSubject<DropdownFacetItem[]>,
            assign: (facet: DropdownFacet) => void
        }> = {
            assetGroupName: {
                subject: this.assetGroupsSubject,
                assign: f => this.assetGroupsFacet = f
            },
            assetType: {
                subject: this.assetTypesSubject,
                assign: f => this.assetTypesFacet = f
            },
            colorChannel: {
                subject: this.colorChannelsSubject,
                assign: f => this.colorChannelsFacet = f
            },
            fileType: {
                subject: this.fileTypesSubject,
                assign: f => this.fileTypesFacet = f
            },
            p_cat: {
                subject: this.productCategoriesSubject,
                assign: f => this.productCategoriesFacet = f
            }
        };

        const config = facetConfigMap[facet.key];
        if (!config) return;

        config.assign(facet);

        const previous = config.subject.getValue();
        const updated = this.syncFacetSelection(values, previous);

        config.subject.next(updated);
    };

    private renderDateRangeFacetsCB = (facet: any) => {
        switch (facet.key) {
            case this.DEFAULT_SORT_FIELD:
                this.assetDateRangeFacet = facet;
                break;

            default:
                break;
        }
    }

    private updateSearchParameters(): void {
        let searchStateParams = this.searchStateParamsSubject.getValue();
        const skip = searchStateParams.pageIndex * searchStateParams.pageSize;
        const searchParameters = {
            top: searchStateParams.pageSize,
            skip: skip,
            orderby: `${searchStateParams.orderBy} ${searchStateParams.sortDirection}`,
            searchFields: 'search_keys'
        }

        this.azureSearchService?.updateSearchParameters(searchParameters);
    }

    private updateSearchState(patch: Partial<SearchState>) {
        const current = this.searchStateParamsSubject.getValue();
        this.searchStateParamsSubject.next({
            ...current,
            ...patch
        });
    }

    private syncFacetSelection(values: DropdownFacetItem[], previous: DropdownFacetItem[]): DropdownFacetItem[] {
        previous.forEach(item => {
            const match = values.find(v => v.value === item.value);
            if (match) {
                match.selected = item.selected;
            }
        });
        return values;
    }

    setFilters(filters: any): void {
        this.filterBehaviorSubject.next(filters);
    }

    getFilters(): any {
        return this.filterBehaviorSubject.getValue();
    }

    clearFilters(): void {
        this.filterBehaviorSubject.next(null);
    }

    setSearchQuery(searchQuery: string) {
        this.searchQuerySubject.next(searchQuery);
    }

    search(searchQuery: string = '') {
        if (!this.azureSearchService) return;
        const query = searchQuery ?? this.searchQuerySubject.getValue();

        this.pageCache.clear();
        this.clearNavigationContextAndCache();

        this.updateSearchState({ pageIndex: 0 });
        this.azureSearchService?.setInput(query);
        this.loadingSubject.next(true);
        this.updateSearchParameters();
        this.azureSearchService?.search();
    }

    sort(orderby: string, direction: 'asc' | 'desc') {
        this.updateSearchState({ pageIndex: 0, orderBy: orderby, sortDirection: direction });
        this.updateSearchParameters();
        this.azureSearchService?.search();
    }

    pageChange(pageIndex: number, pageSize: number) {
        this.updateSearchState({ pageIndex, pageSize });
        this.loadingSubject.next(true);
        this.updateSearchParameters();
        this.azureSearchService?.search();
    }

    updatePageSize(pageSize: number) {
        this.updateSearchState({ pageSize });
    }

    updateSelectedItems(facetType: FacetType, selectedItems: DropdownFacetItem[]) {
        const facetMap = {
            assetTypes: this.assetTypesFacet,
            assetGroups: this.assetGroupsFacet,
            colorChannels: this.colorChannelsFacet,
            fileTypes: this.fileTypesFacet,
            productCategories: this.productCategoriesFacet
        };

        const facet = facetMap[facetType];

        if (!facet) return;

        facet.filterClause = '';

        const selectedSet = new Set(selectedItems?.map(i => i.value));

        Object.values(facet.values).forEach(value => {
            value.selected = selectedSet.has(value.value);
        });

        (selectedItems ?? []).forEach(item => {
            const facetItem = facet.values[item.value];
            if (facetItem) {
                facetItem.selected = true;
            }
        });

        this.azureSearchService?.buildDropdownFilter(facet);
    }

    updateDateRange(facetType: 'dateRange', startDate: number | null, endDate: number | null) {
        const facetMap = {
            dateRange: this.assetDateRangeFacet
        };

        const facet = facetMap[facetType];
        if (!facet) return;
        facet.filterLowerBound = startDate ?? 0;
        facet.filterUpperBound = endDate ?? 0;
        facet.filterClause = '';
        this.azureSearchService?.buildDateRangeFilter(facet);
    }

    updateLoading(loading: boolean) {
        this.loadingSubject.next(loading);
    }

    reset() {
        this.updateSelectedItems('assetTypes', []);
        this.updateSelectedItems('assetGroups', []);
        this.updateSelectedItems('colorChannels', []);
        this.updateSelectedItems('fileTypes', []);
        this.updateSelectedItems('productCategories', []);
        this.updateDateRange('dateRange', null, null);

        this.searchStateParamsSubject.next(initialSearchState);
        this.searchQuerySubject.next('');
        this.loadingSubject.next(true);
    }

    // Page cache
    private pageCache = new Map<number, SearchAsset[]>();

    // Navigation context
    private navigationContextSubject = new BehaviorSubject<{ currentIndex: number; } | null>(null);
    navigationContext$ = this.navigationContextSubject.asObservable();

    // Derived state
    canNavigate$ = this.navigationContext$.pipe(map(ctx => !!ctx));
    canNext$ = this.navigationContext$.pipe(map(ctx => !!ctx && ctx.currentIndex < this.getTotalCount() - 1));
    canPrevious$ = this.navigationContext$.pipe(map(ctx => !!ctx && ctx.currentIndex > 0));

    openAsset(asset: SearchAsset, localIndex: number) {
        const state = this.searchStateParamsSubject.getValue();
        const globalIndex = state.pageIndex * state.pageSize + localIndex;
        this.pageCache.clear();

        // Cache current page
        this.pageCache.set(state.pageIndex, this.assetsSubject.getValue());
        this.navigationContextSubject.next({ currentIndex: globalIndex });

        this.loadPage(state.pageIndex + 1);
    }

    clearNavigationContextAndCache() {
        this.pageCache.clear();
        this.navigationContextSubject.next(null);
    }

    private getCurrentState() {
        return this.searchStateParamsSubject.getValue();
    }

    private getTotalCount() {
        return this.totalAssetsCountSubject.getValue();
    }

    private getPageFromGlobalIndex(globalIndex: number) {
        const state = this.getCurrentState();
        return Math.floor(globalIndex / state.pageSize);
    }

    private getLocalIndex(globalIndex: number) {
        const state = this.getCurrentState();
        return globalIndex % state.pageSize;
    }

    private async loadPage(pageIndex: number): Promise<void> {
        if (this.pageCache.has(pageIndex)) {
            return;
        }

        const state = this.getCurrentState();
        const skip = pageIndex * state.pageSize;

        this.azureSearchService.updateSearchParameters({ top: state.pageSize, skip: skip });
        const originalProcessor = this.searchResultCB;

        return new Promise(resolve => {
            this.azureSearchService.setResultsProcessor((data: any) => {
                this.pageCache.set(pageIndex, data.results);
                // Restore original processor
                this.azureSearchService.setResultsProcessor(originalProcessor);
                resolve();
            });

            this.azureSearchService.search({}, ['assetId']);
        });
    }

    async goNext(): Promise<SearchAsset | null> {
        const ctx = this.navigationContextSubject.getValue();
        if (!ctx) return null;

        const total = this.getTotalCount();

        if (ctx.currentIndex >= total - 1) {
            return null; // Last asset
        }

        const nextIndex = ctx.currentIndex + 1;
        const nextPage = this.getPageFromGlobalIndex(nextIndex);
        const localIndex = this.getLocalIndex(nextIndex);

        await this.loadPage(nextPage);

        const pageData = this.pageCache.get(nextPage);
        if (!pageData) return null;

        this.navigationContextSubject.next({ currentIndex: nextIndex });

        return pageData[localIndex];
    }

    async goPrevious(): Promise<SearchAsset | null> {
        const ctx = this.navigationContextSubject.getValue();
        if (!ctx) return null;

        if (ctx.currentIndex <= 0) {
            return null; // First asset
        }

        const prevIndex = ctx.currentIndex - 1;
        const prevPage = this.getPageFromGlobalIndex(prevIndex);
        const localIndex = this.getLocalIndex(prevIndex);

        await this.loadPage(prevPage);

        const pageData = this.pageCache.get(prevPage);
        if (!pageData) return null;

        this.navigationContextSubject.next({ currentIndex: prevIndex });

        return pageData[localIndex];
    }

}